<script setup lang="ts">
import type {
  CmsElementProductSlider,
  SliderElementConfig,
} from "@shopware/composables";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import type { ComputedRef } from "vue";
import { useCmsElementConfig } from "#imports";

const props = defineProps<{
  content: CmsElementProductSlider;
}>();

const { getConfigValue } = useCmsElementConfig(props.content);

const products = computed(() => props.content?.data?.products ?? []);

const productSlider = ref(null);

const productSwiperSlider = useSwiper(productSlider, {
  effect: "slide",
  loop: true,
  grabCursor: true,
  slidesPerView: 1,
  breakpoints: {
    540: {
      slidesPerView: 2,
    },
    768: {
      slidesPerView: 3,
    },
    1024: {
      slidesPerView: 4,
    },
    1200: {
      slidesPerView: 5,
    },
    1520: {
      slidesPerView: 6,
    },
  },
});

const autoplay = computed(() => getConfigValue("rotate"));
const title = computed(() => getConfigValue("title"));
const border = computed(() => getConfigValue("border"));

const isSliderReady = ref(false);
onMounted(() => {
  isSliderReady.value = true;
});
</script>
<template>
  <section
    class="cms-element-product-slider pt-10 md:pt-14 lg:pt-20 pb-20 md:pb-28 lg:pb-40"
    :class="{ 'py-5 border border-secondary-300': border }"
  >
    <div class="container">
      <h3 v-if="title" class="h3_head">{{ title }}</h3>
    </div>
    <div
      v-if="isSliderReady"
      class="product-wrapper product_slider m-0 slick-initialized slick-slider overflow-hidden relative"
    >
      <div
        @click="productSwiperSlider.prev()"
        class="swiper-cust-button-prev bg-cust_cream text-cust_gray03"
      >
      <i class="fa-solid fa-arrow-left"></i>
      </div>
      <swiper-container
        ref="productSlider"
        :autoplay="true"
        class="-mx-[20px] px-[20px]"
      >
        <swiper-slide v-for="product in products" :key="product.id">
          <ProductCard
            class="h-full"
            :product="product"
            :layout-type="getConfigValue('boxLayout')"
            :display-mode="getConfigValue('displayMode')"
          ></ProductCard>
        </swiper-slide>
      </swiper-container>
      <div
        @click="productSwiperSlider.next()"
        class="swiper-cust-button-next bg-cust_cream text-cust_gray03"
      >
        <i class="fa-solid fa-arrow-right"></i>
      </div>
    </div>
    <div v-else class="flex">
      <div
        v-for="index in 4"
        :key="index"
        data-testid="loading"
        class="w-1/2 lg:w-1/3 xl:w-1/4 px-2"
      >
        <ProductCardSkeleton class="w-full mb-8 px-1 sm:mr-8 mb-8" />
      </div>
    </div>
  </section>
</template>

<style scoped>
.product-wrapper :deep(.swiper) {
  overflow: visible;
}

.swiper-container {
  overflow: visible;
  width: 70%;
  margin-left: auto;
  margin-right: auto;
}
</style>
